// Renders the queue of slide-up automation prompts at the bottom of the
// screen and the auto-dismiss toast stack at the top-right. Mounted once,
// globally, by App.js so any page can fire automations without owning UI.
//
// Z-ORDER NOTE: the prompt stack (z-220) and toasts (z-230) deliberately sit
// ABOVE the Workflows bottom sheet (backdrop 100 / panel 101) and its
// elevated header pipeline (102), so automations fired by a stage change —
// via the kanban header pipeline or the sheet's StageIndicator — slide up
// over the open sheet and stay fully interactive.

import React, { useState, useEffect } from 'react';
import { useAutomation } from '../context/AutomationContext';
import { useTheme } from '../context/ThemeContext';
import { bottomClear, vh } from '../utils/browserChrome';

function AssigneeMultiSelect({ companyUsers, value, onChange, theme, mode }) {
  const [open, setOpen] = useState(false);
  const selected = (companyUsers || []).filter(u => value.includes(u.uid));
  const label = selected.length === 0
    ? 'No default — select assignee(s)'
    : selected.map(u => (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : (u.email || '')).join(', ');

  const triggerBg = mode === 'night' ? '#1e1c18' : '#ffffff';
  const triggerBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const triggerText = selected.length === 0 ? '#888' : (mode === 'night' ? '#f4f1eb' : '#1a1a1a');
  const dropdownBg = mode === 'night' ? '#1e1c18' : '#fff';
  const dropdownBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const itemText = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const itemHover = mode === 'night' ? '#2a2824' : '#f4f1eb';
  const checkBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const checkFill = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const checkStroke = mode === 'night' ? '#0e0d0b' : '#fff';

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '8px 12px',
          backgroundColor: triggerBg,
          border: `1px solid ${triggerBorder}`,
          borderRadius: '2px',
          cursor: 'pointer',
          fontSize: '12px',
          color: triggerText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
          <polyline points="2,3 4.5,6.5 7,3" stroke={triggerBorder} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {open && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 250 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: dropdownBg,
              border: `1px solid ${dropdownBorder}`,
              maxHeight: '220px',
              overflowY: 'auto',
              zIndex: 251,
              fontFamily: 'inherit',
            }}
          >
            {(companyUsers || []).length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: '12px', color: theme.textFaint, fontStyle: 'italic' }}>No team members.</div>
            ) : (companyUsers || []).map(u => {
              const name = (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : (u.email || u.uid);
              const checked = value.includes(u.uid);
              return (
                <div
                  key={u.uid}
                  onClick={() => onChange(checked ? value.filter(id => id !== u.uid) : [...value, u.uid])}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: itemText,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: `1px solid ${mode === 'night' ? '#2a2824' : '#eee'}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = itemHover}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    width: '13px', height: '13px', flexShrink: 0,
                    border: `1.5px solid ${checkBorder}`,
                    backgroundColor: checked ? checkFill : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && (
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                        <polyline points="1.5,4.5 3.5,6.5 7.5,2.5" stroke={checkStroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function PromptCard({ prompt, onConfirm, onDismiss, theme, mode }) {
  const [assigneeIds, setAssigneeIds] = useState(prompt.defaultIds || []);
  const [sendEmail, setSendEmail] = useState(!!prompt.sendEmail);
  const [busy, setBusy] = useState(false);

  const ruleDesc = prompt.rule.description || '(unnamed task)';
  const taskKind = prompt.rule.taskType === 'decision' ? 'Push Task' : 'Task';

  const handleConfirm = async () => {
    setBusy(true);
    await onConfirm(prompt.id, { assigneeIds, sendEmail });
    setBusy(false);
  };

  const handleDismiss = async () => {
    setBusy(true);
    await onDismiss(prompt.id);
    setBusy(false);
  };

  const cardBg = mode === 'night' ? '#131210' : '#ffffff';
  const cardBorderTop = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const labelColor = mode === 'night' ? '#c8c4be' : '#3d3d3d';
  const titleColor = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const checkBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const checkFill = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const checkStroke = mode === 'night' ? '#0e0d0b' : '#fff';
  const confirmBg = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const confirmColor = mode === 'night' ? '#0e0d0b' : '#f4f1eb';
  const dismissColor = mode === 'night' ? '#c8c4be' : '#3d3d3d';
  const dismissBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';

  return (
    <div style={{
      backgroundColor: cardBg,
      borderTop: `1px solid ${cardBorderTop}`,
      padding: '16px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      animation: 'autoSlideUp 0.2s ease-out',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: labelColor, margin: '0 0 4px 0', fontWeight: '600' }}>
            Automation — {taskKind} on {prompt.newStage}
          </p>
          <p style={{ fontSize: '14px', fontWeight: '700', color: titleColor, margin: 0, lineHeight: '1.3' }}>
            {ruleDesc}
          </p>
          {prompt.job?.jobName && (
            <p style={{ fontSize: '11px', color: labelColor, margin: '4px 0 0 0' }}>
              For: {prompt.job.jobName}
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: labelColor, fontWeight: '600', flexShrink: 0 }}>Assign to</span>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <AssigneeMultiSelect
            companyUsers={prompt.companyUsers}
            value={assigneeIds}
            onChange={setAssigneeIds}
            theme={theme}
            mode={mode}
          />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{
          width: '14px', height: '14px',
          border: `1.5px solid ${checkBorder}`,
          backgroundColor: sendEmail ? checkFill : 'transparent',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {sendEmail && (
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
              <polyline points="1.5,4.5 3.5,6.5 7.5,2.5" stroke={checkStroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={e => setSendEmail(e.target.checked)}
          style={{ display: 'none' }}
        />
        <span style={{ fontSize: '12px', color: titleColor }}>Send email notification</span>
      </label>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleDismiss}
          disabled={busy}
          style={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: dismissColor,
            border: `1px solid ${dismissBorder}`,
            borderRadius: '2px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            opacity: busy ? 0.6 : 1,
          }}
        >Dismiss</button>
        <button
          onClick={handleConfirm}
          disabled={busy}
          style={{
            padding: '8px 18px',
            backgroundColor: confirmBg,
            color: confirmColor,
            border: `1px solid ${confirmBg}`,
            borderRadius: '2px',
            fontSize: '12px',
            fontWeight: '700',
            cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            opacity: busy ? 0.6 : 1,
          }}
        >{busy ? 'Working…' : 'Confirm'}</button>
      </div>
    </div>
  );
}

export default function AutomationOverlay() {
  const { pendingPrompts, toasts, confirmPrompt, dismissPrompt, removeToast } = useAutomation();
  const { theme, mode } = useTheme();

  // Inject keyframes for the slide-up animation once on mount.
  useEffect(() => {
    if (document.getElementById('automation-overlay-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'automation-overlay-keyframes';
    style.textContent = `@keyframes autoSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`;
    document.head.appendChild(style);
  }, []);

  const toastBg = mode === 'night' ? '#131210' : '#ffffff';
  const toastBorder = mode === 'night' ? '#f4f1eb' : '#1a1a1a';
  const toastText = mode === 'night' ? '#f4f1eb' : '#1a1a1a';

  return (
    <>
      {/* Slide-up confirmation prompts (bottom of screen). Stacked. */}
      {pendingPrompts.length > 0 && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          // The stack is pinned to the bottom edge, so it pays both insets:
          // the home indicator and the browser's own bar. Without this the
          // last prompt's Confirm/Dismiss sits under Safari's toolbar.
          bottom: bottomClear('0px'),
          zIndex: 220,
          // Visible viewport, not the theoretical one — see browserChrome.vh().
          maxHeight: vh(60),
          overflowY: 'auto',
          fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {pendingPrompts.map(p => (
            <PromptCard
              key={p.id}
              prompt={p}
              onConfirm={confirmPrompt}
              onDismiss={dismissPrompt}
              theme={theme}
              mode={mode}
            />
          ))}
        </div>
      )}

      {/* Auto-dismiss toasts (top-right). */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 230,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          maxWidth: '360px',
          fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {toasts.map(t => (
            <div
              key={t.id}
              onClick={() => removeToast(t.id)}
              style={{
                backgroundColor: toastBg,
                border: `1px solid ${toastBorder}`,
                borderRadius: '0',
                padding: '10px 14px',
                fontSize: '12px',
                color: toastText,
                cursor: 'pointer',
                lineHeight: '1.4',
              }}
            >{t.message}</div>
          ))}
        </div>
      )}
    </>
  );
}
